import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import type { Attachment, Message, MessageId } from '../fixtures/domain.js';
import { ApiRequestError } from '../lib/api.js';
import type { ComposerHandle } from './Composer.js';
import { authorNameFor, toQuotedPreview, type QuotedPreview } from './MessageQuote.js';

export interface UseComposerDraft {
  /** The staged reply or edit as the composer's strip renders it, or nothing. */
  staged?: QuotedPreview;
  /** Why the last edit was refused, until the next draft starts. */
  notice?: string;
  startReply: (message: Message) => void;
  startEdit: (message: Message) => void;
  cancel: () => void;
  /** Send `text` as a reply to the staged quote, or save it over the message being edited.
   *  Resolves with the new message id when this was a fresh send. */
  submit: (text: string, attachments?: Attachment[]) => Promise<string | undefined>;
}

interface Staged {
  kind: 'reply' | 'edit';
  message: Message;
}

interface DraftConversation {
  send: (
    text: string,
    quotedMessageId?: MessageId,
    attachments?: Attachment[],
  ) => Promise<string | undefined>;
  editMessage: (messageId: MessageId, text: string) => Promise<void>;
  isOwnMessage: (message: Message) => boolean;
  currentUserId: string;
}

/**
 * The reply or edit staged above the composer, shared by the full page and the widget.
 *
 * A hook rather than local state in one panel because a draft that behaved differently
 * on the two surfaces would be a bug nobody could reproduce on the one they were looking at.
 */
export function useComposerDraft(
  channelId: string | undefined,
  convo: DraftConversation,
  counterpartName: string,
  composerRef: RefObject<ComposerHandle | null>,
): UseComposerDraft {
  const [staged, setStaged] = useState<Staged | null>(null);
  const [notice, setNotice] = useState<string>();
  const preEditDraft = useRef('');
  const currentChannel = useRef(channelId);

  // A draft belongs to the conversation it was staged in.
  useEffect(() => {
    currentChannel.current = channelId;
    setStaged(null);
    setNotice(undefined);
  }, [channelId]);

  const restorePreEditDraft = useCallback(
    () => composerRef.current?.insert(preEditDraft.current),
    [composerRef],
  );

  const stage = useCallback(
    (next: Staged | null) => {
      const editing = staged?.kind === 'edit';
      if (next?.kind === 'edit') {
        if (!editing) preEditDraft.current = composerRef.current?.value() ?? '';
        composerRef.current?.insert(next.message.text);
      } else if (editing) {
        restorePreEditDraft();
      }
      setStaged(next);
      setNotice(undefined);
    },
    [staged, composerRef, restorePreEditDraft],
  );

  const startReply = useCallback((message: Message) => stage({ kind: 'reply', message }), [stage]);
  const startEdit = useCallback((message: Message) => stage({ kind: 'edit', message }), [stage]);
  const cancel = useCallback(() => stage(null), [stage]);

  const { send, editMessage } = convo;
  const submit = useCallback(
    async (text: string, attachments?: Attachment[]): Promise<string | undefined> => {
      // Cleared before the await: the send spends the draft, and leaving it staged invites a second one.
      setStaged(null);
      setNotice(undefined);
      if (staged?.kind !== 'edit') return send(text, staged?.message.id, attachments);
      if (attachments?.length) {
        setStaged(staged);
        setNotice('Only the text can be edited. Remove the file to save.');
        throw new Error('an edit cannot carry an attachment');
      }
      if (text.trim() === staged.message.text.trim()) {
        restorePreEditDraft();
        return;
      }
      const startedIn = channelId;
      try {
        await editMessage(staged.message.id, text);
      } catch (error) {
        // Another chat is open by now, and its composer is not this edit's to touch.
        if (currentChannel.current !== startedIn) return;
        if (!isFinal(error)) {
          setStaged(staged);
          throw error;
        }
        setNotice(error.message);
      }
      if (currentChannel.current === startedIn) restorePreEditDraft();
    },
    [send, editMessage, staged, channelId, restorePreEditDraft],
  );

  return {
    ...(staged ? { staged: previewOf(staged, convo, counterpartName) } : {}),
    ...(notice ? { notice } : {}),
    startReply,
    startEdit,
    cancel,
    submit,
  };
}

/** A 4xx (read, past the day, gone, not ours) cannot succeed on retry, so the edit closes with its reason. */
function isFinal(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError && error.status >= 400 && error.status < 500;
}

function previewOf(
  staged: Staged,
  convo: DraftConversation,
  counterpartName: string,
): QuotedPreview {
  if (staged.kind === 'edit') return { ...toQuotedPreview(staged.message), editing: true };
  const { isOwnMessage, currentUserId } = convo;
  const authorName = authorNameFor(staged.message, {
    isOwnMessage,
    currentUserId,
    counterpartName,
  });
  return toQuotedPreview(staged.message, authorName);
}
